import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { Button, Card, Col, message, Modal, Row, Spin, Typography, Input } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Box, Text } from 'atoms';
import { ACL } from 'components/common';
import FilePreviewModal from 'componentsv2/common/FilePreviewModal';
import {
	approveVehicleReplacement,
	rejectVehicleReplacement,
	getApprovalDetailsV2,
} from 'store/approvalSliceV2';

const { Title } = Typography;

const APPROVALS_VEHICLE_REPLACEMENT_PATH = '/approvals/vehicle-replacement';

const PageContainer = styled(Box)`
	padding: 24px;
	background: ${(props) => (props.$fromApproval ? '#fff' : '#f5f5f5')};
	min-height: calc(100vh - 64px);
`;

const HeaderContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
`;

const HeaderLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
`;

const HeaderRight = styled.div`
	display: flex;
	gap: 12px;
`;

const RequestId = styled(Title)`
	margin: 0 !important;
	font-size: 20px;
	font-weight: 600;
`;

const CloseIconButton = styled(Button)`
	border: none;
	box-shadow: none;
	padding: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;

	&:hover {
		background-color: #f5f5f5;
	}
`;

const ActionButtonContainer = styled.div`
	display: flex;
	justify-content: center;
	gap: 16px;
	margin-top: 32px;
	padding-top: 24px;
	border-top: 1px solid #e8e8e8;
`;

const RejectButtonStyled = styled(Button)`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 8px 24px;
	height: 40px;
	background: #fff;
	color: #f44336;
	border: 1.5px solid #f44336;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	min-width: 120px;

	&:hover {
		background: #ffebee;
		border-color: #e57373;
		color: #e57373;
	}

	&:active {
		background: #fff;
		border-color: #d32f2f;
		color: #d32f2f;
	}
`;

const ApproveButtonStyled = styled(Button)`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 8px 24px;
	height: 40px;
	background: #52c41a;
	border-color: #52c41a;
	color: #fff;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	min-width: 120px;

	&:hover {
		background: #73d13d;
		border-color: #73d13d;
	}

	&:active {
		background: #389e0d;
		border-color: #389e0d;
	}
`;

const DetailsCard = styled(Card)`
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const FieldLabel = styled(Text)`
	font-size: 12px;
	color: #666;
	font-weight: 400;
	display: block;
	margin-bottom: 8px;
`;

const FieldValue = styled(Text)`
	font-size: 14px;
	color: #000;
	font-weight: 500;
	display: block;

	/* Ensure children are valid React nodes */
	& > * {
		display: inline;
	}
`;

const DocumentLink = styled.span`
	color: #1890ff;
	text-decoration: none;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;

	&:hover {
		text-decoration: underline;
		color: #40a9ff;
	}
`;

const { TextArea } = Input;

export default function VehicleReplacementDetailsPageV2() {
	const { id } = useParams();
	const location = useLocation();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [details, setDetails] = useState(null);
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState(false);
	const [approveModalVisible, setApproveModalVisible] = useState(false);
	const [rejectModalVisible, setRejectModalVisible] = useState(false);
	const [filePreviewVisible, setFilePreviewVisible] = useState(false);
	const [previewFileKey, setPreviewFileKey] = useState(null);
	const [previewFileName, setPreviewFileName] = useState('');
	const [comments, setComments] = useState('');
	const [rejectionReason, setRejectionReason] = useState('');

	const fromApproval =
		location.state?.fromApproval || location.pathname.startsWith('/approvals/');

	const handleViewDocument = (fileKey, fileName = '') => {
		if (!fileKey) return;
		setPreviewFileKey(fileKey);
		setPreviewFileName(fileName);
		setFilePreviewVisible(true);
	};

	const handleFilePreviewClose = () => {
		setFilePreviewVisible(false);
		setPreviewFileKey(null);
		setPreviewFileName('');
	};

	const buildReplacementDetails = (approvalData) => {
		const entityData = approvalData.approval.entity_data || {};
		const entityDetails = approvalData.entity_details || {};

		return {
			vehicle_number:
				entityDetails.new_vehicle_number ||
				entityDetails.vehicle_number ||
				entityData.new_vehicle_number ||
				entityData.vehicle_number ||
				'-',
			vendor_name: entityDetails.vendor_name || entityData.vendor_name || '-',
			vehicle_type:
				entityDetails.vehicle_type ||
				entityDetails.vehicle_type_name ||
				entityData.vehicle_type ||
				entityData.vehicle_type_name ||
				'-',
			rc_document_url: entityDetails.rc_document_url || entityData.rc_document_url || null,
			rc_document_name: entityDetails.rc_document_name || entityData.rc_document_name || null,
			insurance_document_url:
				entityDetails.insurance_document_url || entityData.insurance_document_url || null,
			insurance_document_name:
				entityDetails.insurance_document_name || entityData.insurance_document_name || null,
			city: entityDetails.city || entityData.city || '-',
			created_by: approvalData.approval.requested_by_name || '-',
		};
	};

	const fetchApprovalDetails = async () => {
		setLoading(true);
		try {
			const approvalData = await dispatch(getApprovalDetailsV2({ approval_id: id }));

			if (!approvalData || !approvalData.approval) {
				message.error('Approval not found');
				navigate(APPROVALS_VEHICLE_REPLACEMENT_PATH);
				return;
			}

			setDetails(buildReplacementDetails(approvalData));
		} catch (error) {
			console.error('Error fetching approval details:', error);
			message.error('Failed to fetch approval details');
			navigate(APPROVALS_VEHICLE_REPLACEMENT_PATH);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (id) {
			fetchApprovalDetails();
		}
	}, [id]);

	const handleApproveClick = () => {
		setComments('');
		setApproveModalVisible(true);
	};

	const handleRejectClick = () => {
		setRejectionReason('');
		setRejectModalVisible(true);
	};

	const handleApproveConfirm = async () => {
		if (!comments.trim()) {
			message.error('Comments are required for approval');
			return;
		}

		const approvalId = location.state?.approvalId || id;
		if (!approvalId) {
			message.error('Approval ID not found');
			return;
		}

		setActionLoading(true);
		try {
			const result = await dispatch(
				approveVehicleReplacement({
					approval_id: approvalId,
					comments,
				})
			);

			if (result.success) {
				setApproveModalVisible(false);
				setComments('');
				message.success('Vehicle replacement approved successfully');
				navigate(APPROVALS_VEHICLE_REPLACEMENT_PATH);
			}
		} catch (error) {
			console.error('Error approving vehicle replacement:', error);
			message.error('Failed to approve vehicle replacement');
		} finally {
			setActionLoading(false);
		}
	};

	const handleRejectConfirm = async () => {
		if (!rejectionReason.trim()) {
			message.error('Rejection reason is required');
			return;
		}

		const approvalId = location.state?.approvalId || id;
		if (!approvalId) {
			message.error('Approval ID not found');
			return;
		}

		setActionLoading(true);
		try {
			const result = await dispatch(
				rejectVehicleReplacement({
					approval_id: approvalId,
					rejection_reason: rejectionReason,
				})
			);

			if (result.success) {
				setRejectModalVisible(false);
				setRejectionReason('');
				message.success('Vehicle replacement rejected successfully');
				navigate(APPROVALS_VEHICLE_REPLACEMENT_PATH);
			}
		} catch (error) {
			console.error('Error rejecting vehicle replacement:', error);
			message.error('Failed to reject vehicle replacement');
		} finally {
			setActionLoading(false);
		}
	};

	if (loading) {
		return (
			<PageContainer $fromApproval={fromApproval}>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
					<Spin size="large" tip="Loading vehicle replacement details..." />
				</Box>
			</PageContainer>
		);
	}

	if (!details) {
		return (
			<PageContainer $fromApproval={fromApproval}>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
					<Text>Vehicle replacement details not found</Text>
				</Box>
			</PageContainer>
		);
	}

	return (
		<PageContainer $fromApproval={fromApproval}>
			{fromApproval && (
				<HeaderContainer>
					<HeaderLeft>
						<RequestId>Request ID: {id}</RequestId>
					</HeaderLeft>
					<HeaderRight>
						<CloseIconButton
							icon={<CloseOutlined />}
							onClick={() => navigate(APPROVALS_VEHICLE_REPLACEMENT_PATH)}
						/>
					</HeaderRight>
				</HeaderContainer>
			)}

			<DetailsCard>
				<Row gutter={[24, 24]}>
					<Col xs={24} sm={8}>
						<FieldLabel>Vehicle Number</FieldLabel>
						<FieldValue>{details.vehicle_number || '-'}</FieldValue>
					</Col>
					<Col xs={24} sm={8}>
						<FieldLabel>Vendor Name</FieldLabel>
						<FieldValue>{details.vendor_name || '-'}</FieldValue>
					</Col>
					<Col xs={24} sm={8}>
						<FieldLabel>Vehicle Type</FieldLabel>
						<FieldValue>{details.vehicle_type || '-'}</FieldValue>
					</Col>
					<Col xs={24} sm={8}>
						<FieldLabel>RC Document</FieldLabel>
						<FieldValue>
							{details.rc_document_url || details.rc_document_name ? (
								<DocumentLink
									onClick={() =>
										handleViewDocument(details.rc_document_url, 'RC Document')
									}
								>
									{details.rc_document_name || 'View RC Document'}
								</DocumentLink>
							) : (
								'-'
							)}
						</FieldValue>
					</Col>
					<Col xs={24} sm={8}>
						<FieldLabel>Insurance Document</FieldLabel>
						<FieldValue>
							{details.insurance_document_url || details.insurance_document_name ? (
								<DocumentLink
									onClick={() =>
										handleViewDocument(
											details.insurance_document_url,
											'Insurance Document'
										)
									}
								>
									{details.insurance_document_name || 'View Insurance Document'}
								</DocumentLink>
							) : (
								'-'
							)}
						</FieldValue>
					</Col>
					<Col xs={24} sm={8}>
						<FieldLabel>City</FieldLabel>
						<FieldValue>{details.city || '-'}</FieldValue>
					</Col>
					<Col xs={24} sm={8}>
						<FieldLabel>Created By</FieldLabel>
						<FieldValue>{details.created_by || '-'}</FieldValue>
					</Col>
				</Row>
			</DetailsCard>

			{fromApproval && (
				<ActionButtonContainer>
					<ACL action="update" module="handle_vehicle_replacement_approval">
						<RejectButtonStyled onClick={handleRejectClick} disabled={actionLoading}>
							<CloseOutlined />
							Reject
						</RejectButtonStyled>
					</ACL>
					<ACL action="update" module="handle_vehicle_replacement_approval">
						<ApproveButtonStyled onClick={handleApproveClick} disabled={actionLoading}>
							<CheckOutlined />
							Approve
						</ApproveButtonStyled>
					</ACL>
				</ActionButtonContainer>
			)}

			{/* Approve Modal */}
			<Modal
				title="Approve Vehicle Replacement"
				open={approveModalVisible}
				onOk={handleApproveConfirm}
				onCancel={() => {
					setApproveModalVisible(false);
					setComments('');
				}}
				okText="Approve"
				cancelText="Cancel"
				confirmLoading={actionLoading}
				okButtonProps={{
					disabled: actionLoading || !comments.trim(),
				}}
			>
				<Box>
					<Text
						fontSize="14px"
						color="#333"
						mb={1}
						display="block"
						style={{ textAlign: 'left', marginBottom: '8px' }}
					>
						Comments <span style={{ color: '#ff4d4f' }}>*</span>
					</Text>
					<TextArea
						rows={4}
						value={comments}
						onChange={(e) => setComments(e.target.value)}
						placeholder="Enter comments for approval"
						maxLength={500}
						showCount
					/>
				</Box>
			</Modal>

			{/* Reject Modal */}
			<Modal
				title="Reject Vehicle Replacement"
				open={rejectModalVisible}
				onOk={handleRejectConfirm}
				onCancel={() => {
					setRejectModalVisible(false);
					setRejectionReason('');
				}}
				okText="Reject"
				cancelText="Cancel"
				confirmLoading={actionLoading}
				okButtonProps={{
					disabled: actionLoading || !rejectionReason.trim(),
					danger: true,
				}}
			>
				<Box>
					<Text
						fontSize="14px"
						color="#333"
						mb={1}
						display="block"
						style={{ textAlign: 'left', marginBottom: '8px' }}
					>
						Rejection Reason <span style={{ color: '#ff4d4f' }}>*</span>
					</Text>
					<TextArea
						rows={4}
						value={rejectionReason}
						onChange={(e) => setRejectionReason(e.target.value)}
						placeholder="Enter rejection reason"
						maxLength={500}
						showCount
					/>
				</Box>
			</Modal>
			<FilePreviewModal
				visible={filePreviewVisible}
				fileKey={previewFileKey}
				fileName={previewFileName}
				entity="vehicle"
				onClose={handleFilePreviewClose}
			/>
		</PageContainer>
	);
}
